import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { normalizeTotpCode, toAsciiDigits } from "@vital/shared";
import { useAuth } from "../context/AuthContext";
import { AuthBrand } from "../components/AuthBrand";
import { api, ApiError } from "../lib/api";

type VerifyState = {
  challengeToken: string;
};

export function MfaChallengePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, refresh } = useAuth();
  const state = location.state as VerifyState | null;

  const [mode, setMode] = useState<"totp" | "recovery">("totp");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!state?.challengeToken) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const normalized =
        mode === "totp" ? normalizeTotpCode(code) : toAsciiDigits(code).trim();
      const result = await api.mfaVerify(state!.challengeToken, normalized);
      setUser(result.user);
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "کد نامعتبر است");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page">
      <AuthBrand />
      <div className="auth-card">
        <h1>تأیید دو مرحله‌ای</h1>
        <p className="auth-subtitle">کد برنامهٔ احراز هویت یا کد بازیابی را وارد کنید.</p>

        <div className="tab-row">
          <button
            type="button"
            className={mode === "totp" ? "tab tab--active" : "tab"}
            onClick={() => {
              setMode("totp");
              setCode("");
              setError(null);
            }}
          >
            کد TOTP
          </button>
          <button
            type="button"
            className={mode === "recovery" ? "tab tab--active" : "tab"}
            onClick={() => {
              setMode("recovery");
              setCode("");
              setError(null);
            }}
          >
            کد بازیابی
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <label>
            <span>{mode === "totp" ? "کد ۶ رقمی" : "کد بازیابی"}</span>
            <input
              type="text"
              dir="ltr"
              lang="en"
              className="ltr-isolate latin-digits"
              inputMode={mode === "totp" ? "numeric" : "text"}
              maxLength={mode === "totp" ? 6 : 16}
              value={code}
              onChange={(e) => {
                const raw = e.target.value;
                setCode(
                  mode === "totp"
                    ? normalizeTotpCode(raw).slice(0, 6)
                    : toAsciiDigits(raw).slice(0, 16),
                );
              }}
              required
              autoComplete="one-time-code"
              placeholder={mode === "totp" ? "XXXXXX" : "XXXX-XXXX"}
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
            {submitting ? "در حال تأیید…" : "تأیید"}
          </button>
        </form>
      </div>
    </div>
  );
}
